import type { ImageDimensions, ImageValidationConstraints } from '@/shared/media/imageValidationTypes';
import { loadImageElement } from './imageConstraints';

const PNG_SIGNATURE = new Uint8Array([0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]);

const isPngSignature = (bytes: Uint8Array): boolean => {
  if (bytes.length < PNG_SIGNATURE.length) return false;
  for (let i = 0; i < PNG_SIGNATURE.length; i += 1) {
    if (bytes[i] !== PNG_SIGNATURE[i]) return false;
  }
  return true;
};

const readFourCC = (view: DataView, offset: number): string => {
  return String.fromCharCode(
    view.getUint8(offset),
    view.getUint8(offset + 1),
    view.getUint8(offset + 2),
    view.getUint8(offset + 3)
  );
};

type PngTransparencyInspectResult = { ok: true; hasTransparency: boolean } | { ok: false };

export const inspectPngTransparency = async (blob: Blob): Promise<PngTransparencyInspectResult> => {
  try {
    const buffer = await blob.arrayBuffer();
    const bytes = new Uint8Array(buffer);
    if (!isPngSignature(bytes)) return { ok: false };

    const view = new DataView(buffer);
    let offset = 8; // signature
    let hasAlphaByColorType = false;

    while (offset + 8 <= view.byteLength) {
      const chunkLength = view.getUint32(offset, false);
      offset += 4;
      const chunkType = readFourCC(view, offset);
      offset += 4;

      if (offset + chunkLength + 4 > view.byteLength) {
        return { ok: false };
      }

      if (chunkType === 'IHDR') {
        // IHDR data: width(4) height(4) bitDepth(1) colorType(1) ...
        if (chunkLength < 10) return { ok: false };
        const colorType = view.getUint8(offset + 9);
        // 4: grayscale+alpha, 6: truecolor+alpha
        if (colorType === 4 || colorType === 6) {
          hasAlphaByColorType = true;
        }
      }

      if (chunkType === 'tRNS') {
        return { ok: true, hasTransparency: true };
      }

      if (chunkType === 'IEND') {
        break;
      }

      offset += chunkLength + 4; // data + CRC
    }

    return { ok: true, hasTransparency: hasAlphaByColorType };
  } catch {
    return { ok: false };
  }
};

export const readImageDimensionsFromBlob = async (blob: Blob): Promise<ImageDimensions | null> => {
  const objectUrl = URL.createObjectURL(blob);
  try {
    const image = await loadImageElement(objectUrl);
    const width = Math.max(image.naturalWidth || image.width, 0);
    const height = Math.max(image.naturalHeight || image.height, 0);
    if (!width || !height) return null;
    return { width: Math.round(width), height: Math.round(height) };
  } catch {
    return null;
  } finally {
    try {
      URL.revokeObjectURL(objectUrl);
    } catch {
      // ignore
    }
  }
};

export type ImageValidationResult =
  | { ok: true; dimensions: ImageDimensions }
  | { ok: false; message: string; dimensions?: ImageDimensions };

const normalizeMimeType = (value: string | undefined): string => (value ?? '').split(';')[0]?.trim().toLowerCase() || '';

export const validateImageBlob = async (
  blob: Blob,
  constraints?: ImageValidationConstraints
): Promise<ImageValidationResult> => {
  const mimeType = normalizeMimeType(blob.type);

  if (constraints?.allowedMimeTypes && constraints.allowedMimeTypes.length > 0) {
    if (!mimeType) {
      return {
        ok: false,
        message: '无法识别图片格式，请重新选择 JPG/PNG/BMP/WEBP 图片。',
      };
    }
    const allowed = new Set(constraints.allowedMimeTypes.map((t) => normalizeMimeType(t)));
    if (!allowed.has(mimeType)) {
      return {
        ok: false,
        message: `图片格式不支持（${mimeType}），仅支持：${constraints.allowedMimeTypes.join('、')}。`,
      };
    }
  }

  if (constraints?.disallowPngTransparency && mimeType === 'image/png') {
    const result = await inspectPngTransparency(blob);
    if (!result.ok) {
      return {
        ok: false,
        message: 'PNG 图片解析失败，请重新导出为不透明 PNG 后再上传。',
      };
    }
    if (result.hasTransparency) {
      return {
        ok: false,
        message: 'PNG 图片不支持透明通道，请去除透明后再上传。',
      };
    }
  }

  const dimensions = await readImageDimensionsFromBlob(blob);
  if (!dimensions) {
    return { ok: false, message: '无法读取图片尺寸，请更换图片后再试。' };
  }

  const minWidth = constraints?.minWidth;
  const minHeight = constraints?.minHeight;
  const maxWidth = constraints?.maxWidth;
  const maxHeight = constraints?.maxHeight;

  const tooSmall =
    (typeof minWidth === 'number' && dimensions.width < minWidth) ||
    (typeof minHeight === 'number' && dimensions.height < minHeight);
  const tooLarge =
    (typeof maxWidth === 'number' && dimensions.width > maxWidth) ||
    (typeof maxHeight === 'number' && dimensions.height > maxHeight);

  if (tooSmall || tooLarge) {
    const rangeText =
      typeof minWidth === 'number' &&
      typeof minHeight === 'number' &&
      typeof maxWidth === 'number' &&
      typeof maxHeight === 'number' &&
      minWidth === minHeight &&
      maxWidth === maxHeight
        ? `宽高需在 ${minWidth}~${maxWidth} 像素之间`
        : `尺寸需满足：宽 ${minWidth ?? '-'}~${maxWidth ?? '-'}，高 ${minHeight ?? '-'}~${maxHeight ?? '-'}`;
    return {
      ok: false,
      message: `图片分辨率不符合要求：${rangeText}，当前为 ${dimensions.width}×${dimensions.height}。`,
      dimensions,
    };
  }

  return { ok: true, dimensions };
};

